import React, { useEffect } from "react";
import axios from "axios";
import { jsPDF } from "jspdf";
import { Box } from "@mui/material";
import { styled } from "@mui/system";
import {
  useCreateReportMutation,
  useGeneratePresignedUrlMutation,
} from "../../../services/reportService";
import { useFetchCurrentUserQuery } from "../../../services/profileService";
import useAuth from "../../../hooks/useAuth";

import Firsttable from "../../report/component/Firsttable";
import DashboardCharts from "../../report/component/DashboardCharts";
import DataComp from "../../report/component/DataComp";
import WorkData from "../../report/component/WorkData";
import ReportHeader from "../../../components/Common/ReportHeader";
import ReportFooter from "../../../components/Common/ReportFooter";

import styles from "../ManagerView.module.css";
import {
  captureAndAddRoleReviewContent,
  captureRoleReviewHeaderFooter,
} from "../../../utils/roleReviewPdf";

const SectionWrap = styled(Box)(() => ({
  padding: "50px 0",
}));

const ContainerWrap = styled(Box)(() => ({
  width: "100%",
  maxWidth: "1170px",
  paddingLeft: "15px",
  paddingRight: "15px",
  marginLeft: "auto",
  marginRight: "auto",
}));

const RowWrap = styled(Box)(() => ({
  display: "flex",
  flexWrap: "wrap",
  marginLeft: "-15px",
  marginRight: "-15px",
}));

const Col12Wrap = styled(Box)(() => ({
  flex: "0 0 auto",
  width: "100%",
  maxWidth: "100%",
  paddingLeft: "15px",
  paddingRight: "15px",
}));

const HiddenReportGenerator = ({
  userId,
  action,
  existingPdfPath,
  onComplete,
}) => {
  const { users } = useAuth();
  const managerId = users?.manager_id;
  const role = users?.role;

  const [generatePresignedUrl] = useGeneratePresignedUrlMutation();
  const [createReport] = useCreateReportMutation();
  const { data: userData } = useFetchCurrentUserQuery(userId);

  useEffect(() => {
    // Wait for components to mount and fetch data before capturing
    const timeout = setTimeout(async () => {
      try {
        const pdf = new jsPDF("p", "mm", "a4");

        const headerElement = document.getElementById("pdfHeader-hidden");
        const footerElement = document.getElementById("pdfFooter-hidden");
        const headerAssets = await captureRoleReviewHeaderFooter({
          headerElement,
          footerElement,
          forceHFClass: styles.forceHF,
          scale: 2,
        });

        const contentElement = document.getElementById("pdfContent-hidden");
        await captureAndAddRoleReviewContent(pdf, {
          element: contentElement,
          headerAssets,
          styles,
          scale: 2,
        });

        const pdfBlob = pdf.output("blob");

        // Determine filename to upload to Azure
        let fileName;
        if (existingPdfPath) {
          fileName = existingPdfPath; // Use existing, so we don't need a DB update
        } else {
          const today = new Date();
          const formattedDate = `${(today.getMonth() + 1)
            .toString()
            .padStart(2, "0")}-${today.getDate().toString().padStart(2, "0")}-${today.getFullYear()}`;
          const employeeName = userData?.data?.name.replace(/\s+/g, "_") || "User";
          fileName = `RoleReview–${employeeName}-${formattedDate}.pdf`;
        }
        
        // Sometimes existingPdfPath might be full path, we just need basename for generation API
        const fileBasename = fileName.split("/").pop();

        // Upload to Azure
        const presignedUrlResponse = await generatePresignedUrl({
          file_name: fileBasename,
          file_type: "application/pdf",
        });

        const presignedUrl = presignedUrlResponse?.data?.data?.presigned_url;

        if (presignedUrl) {
          await axios.put(presignedUrl, pdfBlob, {
            headers: {
              "x-ms-blob-type": "BlockBlob",
              "Content-Type": "application/pdf",
            },
          });

          // Only create report DB record if it never existed
          if (!existingPdfPath) {
            const formData = new FormData();
            formData.append("user_id", userId);
            formData.append("manager_id", managerId ?? userId);
            formData.append("file", fileBasename);
            formData.append("role", role);
            await createReport(formData);
          }

          // Open or Download
          const downloadBlobUrl = window.URL.createObjectURL(pdfBlob);
          if (action === "view") {
            window.open(downloadBlobUrl, "_blank");
          } else {
            const downloadLink = document.createElement("a");
            downloadLink.href = downloadBlobUrl;
            downloadLink.setAttribute("download", fileBasename);
            document.body.appendChild(downloadLink);
            downloadLink.click();
            document.body.removeChild(downloadLink);
            window.URL.revokeObjectURL(downloadBlobUrl);
          }
        }

        // Notify parent we are done
        if (onComplete) onComplete(true);

      } catch (error) {
        console.error("Error generating hidden PDF:", error);
        if (onComplete) onComplete(false);
      }
    }, 2500); // give time for charts to render

    return () => clearTimeout(timeout);
  }, [
    userId,
    managerId,
    role,
    action,
    existingPdfPath,
    generatePresignedUrl,
    createReport,
    userData,
    onComplete,
  ]);

  return (
    <Box
      sx={{
        position: "absolute",
        top: "-9999px",
        left: "-9999px",
        width: "1170px",
        opacity: 0,
        zIndex: -100,
        pointerEvents: "none",
      }}
    >
      <div id="pdfHeader-hidden">
        <ReportHeader />
      </div>
      <SectionWrap component="section" sx={{ position: "relative" }}>
        <ContainerWrap>
          <RowWrap>
            <Col12Wrap>
              <Box id="pdfContent-hidden" sx={{ marginBottom: 0, paddingBottom: 0 }}>
                <Box id="firstPage-hidden">
                  <Firsttable userId={userId} />
                  <DashboardCharts userId={userId} />
                  <Box id="dataComp-hidden">
                    <DataComp userId={userId} />
                  </Box>
                </Box>
                <Box id="secondPage-hidden" style={{ marginTop: "30px" }}>
                  <WorkData userId={userId} />
                </Box>
              </Box>
            </Col12Wrap>
          </RowWrap>
        </ContainerWrap>
      </SectionWrap>
      <div id="pdfFooter-hidden">
        <ReportFooter />
      </div>
    </Box>
  );
};

export default HiddenReportGenerator;
